import createClient, { type Middleware } from 'openapi-fetch'

import { env } from '@/lib/env'

import type { paths } from './schema'
import { expiresWithin } from './token'

const TOKEN_KEY = 'dealerdrive.access_token'
const TENANT_KEY = 'dealerdrive.tenant_id'

type Listener = () => void

/**
 * A single string persisted in localStorage that React can subscribe to
 * (`useSyncExternalStore`). Storage may be unavailable (private mode), in which
 * case the value simply does not survive a reload.
 */
function persistedValue(key: string) {
  const listeners = new Set<Listener>()
  const notify = () => {
    for (const listener of listeners) listener()
  }

  // Keep tabs in step: signing out in one tab signs out the others.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
      if (event.key === key || event.key === null) notify()
    })
  }

  return {
    get(): string | null {
      try {
        return localStorage.getItem(key)
      } catch {
        return null
      }
    },
    set(value: string | null) {
      try {
        if (value) localStorage.setItem(key, value)
        else localStorage.removeItem(key)
      } catch {
        /* storage unavailable (private mode) */
      }
      notify()
    },
    subscribe(listener: Listener): () => void {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

/** Bearer access token for the API. Session rules live in `features/auth/session.ts`. */
export const tokenStore = persistedValue(TOKEN_KEY)

/** Selected dealership. Sent as X-Tenant-ID; the API rejects tenants the user is not a member of. */
export const tenantStore = persistedValue(TENANT_KEY)

/** The refresh endpoint, which is reached without the client below: it has no token to send. */
const REFRESH_PATH = '/api/v1/auth/refresh'
/**
 * How close to its expiry a token is refreshed rather than sent. Wide enough to cover the flight
 * time of the request it would have been attached to and a phone clock a little out of step;
 * narrow enough that a fifteen-minute token is still used for fourteen of them.
 */
const REFRESH_MARGIN_MS = 60_000

let inFlight: Promise<boolean> | null = null

/**
 * Trade the session cookie for a new access token. At most one of these is ever in the air.
 *
 * The refresh token is an httpOnly cookie (ADR 0003): no code here can read it, which is the
 * point, so this is a bare `fetch` with credentials rather than anything that goes through the
 * middleware below — a client that attached an expired bearer token to its own refresh would
 * recurse the first time the answer was 401.
 *
 * Single-flight because a dashboard screen fires half a dozen queries at once: without it, the
 * first refresh rotates the token and the other five present one that has just been spent, which
 * the API reads as a stolen token and answers by ending every session the user has.
 */
export function refreshSession(): Promise<boolean> {
  inFlight ??= (async () => {
    try {
      const response = await fetch(`${env.VITE_API_URL}${REFRESH_PATH}`, {
        method: 'POST',
        credentials: 'include',
      })
      if (!response.ok) return false
      const body = (await response.json()) as { access_token?: unknown }
      if (typeof body.access_token !== 'string') return false
      tokenStore.set(body.access_token)
      return true
    } catch {
      // Offline. The token we hold is not known to be bad, so it is left alone.
      return false
    } finally {
      inFlight = null
    }
  })()
  return inFlight
}

/** The token to send, refreshed first if it is about to run out under the request. */
async function freshToken(): Promise<string | null> {
  const token = tokenStore.get()
  if (token === null || !expiresWithin(token, REFRESH_MARGIN_MS)) return token
  await refreshSession()
  return tokenStore.get()
}

/**
 * The request as it was sent, kept so a 401 can be answered by sending it again.
 *
 * A `Request`'s body is a stream and is spent by the fetch that carried it, so the copy has to
 * be taken on the way out. Weak, so nothing is held once the request object is gone.
 */
const replayable = new WeakMap<Request, Request>()

const auth: Middleware = {
  async onRequest({ request }) {
    const token = await freshToken()
    if (token) request.headers.set('Authorization', `Bearer ${token}`)
    const tenant = tenantStore.get()
    if (tenant) request.headers.set('X-Tenant-ID', tenant)
    if (request.body !== null) replayable.set(request, request.clone())
    return request
  },
  async onResponse({ request, response }) {
    if (response.status !== 401 || !request.headers.has('Authorization')) return response
    // The token was rejected even though it looked live — the clock was out, the secret rotated,
    // or the session was revoked. One refresh decides which, and the request goes again on the
    // new token. Only once: a second 401 is an answer about the user, not about the token.
    if (!(await refreshSession())) {
      tokenStore.set(null)
      return response
    }
    const retry = replayable.get(request) ?? request
    retry.headers.set('Authorization', `Bearer ${tokenStore.get() ?? ''}`)
    return fetch(retry)
  },
}

/** Fully typed client generated from the FastAPI OpenAPI schema (`pnpm api:types`). */
export const api = createClient<paths>({
  baseUrl: env.VITE_API_URL,
  // The session cookie has to ride along, and it is cross-origin whenever the dashboard is not
  // served from the same host as the API (a dev server pointed straight at :8000).
  credentials: 'include',
  // Resolve `fetch` per call rather than at import time, so a later polyfill or test stub applies.
  fetch: (request) => globalThis.fetch(request),
})
api.use(auth)

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: unknown

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

type ErrorEnvelope = {
  error?: { code?: string; message?: string; details?: unknown }
}

/** Convert an openapi-fetch result into data-or-throw so TanStack Query can handle errors. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || result.data === undefined) {
    const body = (result.error ?? {}) as ErrorEnvelope
    throw new ApiError(
      result.response.status,
      body.error?.code ?? 'unknown_error',
      body.error?.message ?? result.response.statusText,
      body.error?.details,
    )
  }
  return result.data
}

/** What one part of a multipart body may hold. A nullish part is left off the form. */
type Part = string | number | boolean | Blob | null | undefined

/** The typed body's view of `Fields`: every file part is the string the contract types it as. */
type Wire<Fields> = { [K in keyof Fields]: Fields[K] extends Blob ? string : Fields[K] }

/**
 * The `body` and `bodySerializer` of a multipart request, from the fields it carries.
 *
 * The generated contract types every file part as a string, because OpenAPI 3.1 has no `binary`
 * format for the generator to map, so a typed `body` cannot hold the `File` the wire carries.
 * The serialiser builds the form from `fields` and never reads the typed body; the cast that
 * bridges the two lives here and nowhere else. A `null` or `undefined` field is left off the
 * form rather than sent as the strings "null" and "undefined"; a `File` keeps its name, so a
 * caller that wants a filename on a bare `Blob` wraps it in one.
 */
export function multipart<Fields extends Record<string, Part>>(
  fields: Fields,
): { body: Wire<Fields>; bodySerializer: () => FormData } {
  return {
    // Reason for the double assertion: `Fields` and `Wire<Fields>` differ only in the file parts,
    // which is exactly the lie the contract tells and this function exists to contain.
    body: fields as unknown as Wire<Fields>,
    bodySerializer: () => {
      const form = new FormData()
      for (const [name, value] of Object.entries(fields)) {
        if (value === null || value === undefined) continue
        // A `File` or `Blob` from whichever realm made it (a test lends Node's), else its text.
        if (typeof value === 'object') form.append(name, value)
        else form.append(name, String(value))
      }
      return form
    },
  }
}

/** `unwrap` for a success that carries no body (204): nothing comes back, a failure still throws. */
export function unwrapNoContent(result: { error?: unknown; response: Response }): void {
  if (!result.response.ok) unwrap({ ...result, data: undefined })
}
